import HttpService from './HttpService';

const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || error.response?.data?.error || defaultMessage;
  const code = error.response?.status;
  // Machine-readable error_code from the response body (distinct from the HTTP
  // status `code`). uploadErrorMessage in StructureEventFiles keys off this to
  // pick code-specific upload copy (FILE_TOO_LARGE, HEIC_DECODE_FAILED, ...);
  // dropping it collapses every upload failure to the generic fallback.
  const errorCode = error.response?.data?.error_code;
  const details = error.response?.data?.details || {};
  // `errors` is the canonical structured-validation key (CLAUDE.md); the
  // backend dual-emits it alongside the legacy `details`. Expose both so
  // forms can surface field-level 400s.
  const errors = error.response?.data?.errors || [];

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code, error_code: errorCode, originalError: error, details, errors };
};

const StructureService = {
  // ---------------------------- Structure (parent) ---------------------------

  /**
   * List a house's structures, each with its events (and files) embedded.
   * @param {number} houseId
   * @returns {Promise<{structures: Array}>}
   */
  getStructures: async (houseId) => {
    try {
      const response = await HttpService.get(`/houses/${houseId}/structures`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching structures');
    }
  },

  /** Get a single structure by id (events + files embedded). */
  getStructure: async (id) => {
    try {
      const response = await HttpService.get(`/structures/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error fetching structure ${id}`);
    }
  },

  /** Create a structure under a house. JSON body. */
  createStructure: async (houseId, data) => {
    try {
      const response = await HttpService.post(`/houses/${houseId}/structures`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating structure');
    }
  },

  /** Update a structure. JSON body. */
  updateStructure: async (id, data) => {
    try {
      const response = await HttpService.put(`/structures/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating structure ${id}`);
    }
  },

  /** Delete a structure (cascades its events + files). */
  deleteStructure: async (id) => {
    try {
      const response = await HttpService.delete(`/structures/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting structure ${id}`);
    }
  },

  // ------------------------------ Event (child) ------------------------------

  /** List events for a structure. */
  getEvents: async (structureId) => {
    try {
      const response = await HttpService.get(`/structures/${structureId}/events`);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching events');
    }
  },

  /** Create an event under a structure. JSON body. */
  createEvent: async (structureId, data) => {
    try {
      const response = await HttpService.post(`/structures/${structureId}/events`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error creating event');
    }
  },

  /** Update an event. JSON body. */
  updateEvent: async (id, data) => {
    try {
      const response = await HttpService.put(`/structure-events/${id}`, data);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error updating event ${id}`);
    }
  },

  /** Delete an event (cascades its files). */
  deleteEvent: async (id) => {
    try {
      const response = await HttpService.delete(`/structure-events/${id}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting event ${id}`);
    }
  },

  // -------------------------- Event files (grandchild) -----------------------

  /**
   * Upload a photo or document to an event.
   * @param {number} eventId
   * @param {File} file
   * @param {'photo'|'document'} kind
   */
  uploadEventFile: async (eventId, file, kind = 'photo') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('kind', kind);
      const response = await HttpService.post(`/structure-events/${eventId}/files`, formData);
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error uploading file');
    }
  },

  /** Delete a file from an event. */
  deleteEventFile: async (fileId) => {
    try {
      const response = await HttpService.delete(`/structure-event-files/${fileId}`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error deleting file ${fileId}`);
    }
  },

  /** Promote a photo to primary. */
  setEventFilePrimary: async (fileId) => {
    try {
      const response = await HttpService.put(`/structure-event-files/${fileId}/primary`);
      return response.data;
    } catch (error) {
      throw formatError(error, `Error setting primary photo ${fileId}`);
    }
  },
};

export default StructureService;
